import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import clsx from 'clsx';
import Card, { CardHeader } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { useListInvestmentPlansQuery } from '../../features/investmentPlans/investmentPlansApi';
import { useCreateInvestmentMutation } from '../../features/investments/adminInvestmentsApi';
import { buildCreateInvestmentSchema } from '../../lib/validators/createInvestmentSchema';
import { formatCurrency, formatDuration, formatRoi } from '../../lib/formatPlan';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

const EMPTY_VALUES = { investment_plan_uuid: '', principal: '', custom_roi_percentage: '', custom_duration_in_days: '' };

/**
 * POST /admin/investments — create-only. There's no PUT/update endpoint
 * for investments in this API, so the historical `/admin/investments/:id/edit`
 * route just redirects to the read-only detail view instead of pretending
 * an edit flow exists (see the `id` check below).
 *
 * Only reachable meaningfully from an investor's detail screen, which
 * passes `{ userUuid, userName }` via router state — user_uuid is never an
 * editable field here, matching the spec's "pre-filled, hidden" framing.
 * Landing here without that state (e.g. a stale bookmark) bounces back to
 * the investor list rather than rendering a form with nothing to attach
 * the investment to.
 *
 * All hooks are called unconditionally on every render — the two
 * early-return cases below only choose what to *render*, never skip a
 * hook call, to stay within the Rules of Hooks.
 */
export default function InvestmentForm() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const userUuid = location.state?.userUuid;
  const userName = location.state?.userName;

  const { data: plansData } = useListInvestmentPlansQuery();
  const plans = plansData ?? [];
  const [createInvestment, { isLoading }] = useCreateInvestmentMutation();
  const [showOverrides, setShowOverrides] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;
  const schema = useMemo(() => buildCreateInvestmentSchema(selectedPlan), [selectedPlan]);

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema), defaultValues: EMPTY_VALUES });

  const onSubmit = async (values) => {
    try {
      const body = {
        user_uuid: userUuid,
        investment_plan_uuid: values.investment_plan_uuid,
        principal: Number(values.principal).toFixed(2),
      };
      // Both optional and independent — only send whichever the admin
      // actually touched, since sending 0 for an unset override would
      // flip is_override: true server-side.
      if (values.custom_roi_percentage !== undefined) body.custom_roi_percentage = values.custom_roi_percentage;
      if (values.custom_duration_in_days !== undefined) body.custom_duration_in_days = values.custom_duration_in_days;

      const investment = await createInvestment(body).unwrap();
      toast.push({ tone: 'success', message: 'Investment created — now pending funding.' });
      navigate(`/admin/investments/${investment.id}`, { replace: true });
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (apiErrors) {
        Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
        return;
      }
      if (err?.status === 409) {
        setError('root', {
          message: "This investor's status changed and is no longer active — refresh their profile before trying again.",
        });
        return;
      }
      setError('root', {
        message: authErrorMessage(err, {
          forbidden: "Your admin account doesn't have the manage-investments permission needed to create investments — ask a super-admin to grant it.",
          fallback: 'Could not create this investment.',
        }),
      });
    }
  };

  if (id) {
    return <Navigate to={`/admin/investments/${id}`} replace />;
  }
  if (!userUuid) {
    return <Navigate to="/admin/investors" replace />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Link to={`/admin/investors/${userUuid}`} className="text-sm text-accent hover:underline">
        ← Back to investor
      </Link>

      <div>
        <h1 className="text-2xl font-semibold text-foreground">Create investment</h1>
        <p className="text-foreground-muted text-sm mt-1">
          For <span className="text-foreground font-medium">{userName || 'this investor'}</span>.
        </p>
      </div>

      <Card>
        <CardHeader title="Investment details" />
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Plan</label>
            <Controller
              name="investment_plan_uuid"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  {plans.map((plan) => (
                    <label
                      key={plan.id}
                      className={clsx(
                        'flex items-start gap-3 rounded-xl border px-3.5 py-3 text-sm cursor-pointer transition-colors',
                        field.value === plan.id ? 'border-accent bg-accent-soft' : 'border-border hover:border-border-strong'
                      )}
                    >
                      <input
                        type="radio"
                        className="mt-1 accent-accent"
                        checked={field.value === plan.id}
                        onChange={() => {
                          field.onChange(plan.id);
                          setSelectedPlanId(plan.id);
                        }}
                      />
                      <span className="flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-medium text-foreground">{plan.name}</span>
                          <Badge tone="success">{formatRoi(plan.roi_percentage)} ROI</Badge>
                        </span>
                        <span className="block text-xs text-foreground-subtle mt-0.5">
                          {formatCurrency(plan.minimum_amount)}
                          {plan.maximum_amount != null ? ` – ${formatCurrency(plan.maximum_amount)}` : '+'} ·{' '}
                          {formatDuration(plan.duration_in_days)}
                        </span>
                      </span>
                    </label>
                  ))}
                  {plans.length === 0 && <p className="text-sm text-foreground-muted">No plans available yet.</p>}
                </div>
              )}
            />
            {errors.investment_plan_uuid && <p className="mt-1.5 text-xs text-danger">{errors.investment_plan_uuid.message}</p>}
          </div>

          <Input
            label="Principal amount (₦)"
            type="number"
            step="0.01"
            helperText={
              selectedPlan
                ? `Plan range: ${formatCurrency(selectedPlan.minimum_amount)}${
                    selectedPlan.maximum_amount != null ? ` – ${formatCurrency(selectedPlan.maximum_amount)}` : '+'
                  }`
                : undefined
            }
            error={errors.principal?.message}
            {...register('principal')}
          />

          <div>
            <button type="button" onClick={() => setShowOverrides((s) => !s)} className="text-xs text-accent hover:underline">
              {showOverrides ? 'Hide overrides' : 'Override ROI % or duration (optional)'}
            </button>

            {showOverrides && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Input
                  label="Override ROI %"
                  type="number"
                  step="0.01"
                  placeholder={selectedPlan ? `Default: ${formatRoi(selectedPlan.roi_percentage)}` : 'Default: plan ROI'}
                  error={errors.custom_roi_percentage?.message}
                  {...register('custom_roi_percentage')}
                />
                <Input
                  label="Override duration (days)"
                  type="number"
                  step="1"
                  placeholder={selectedPlan ? `Default: ${selectedPlan.duration_in_days}` : 'Default: plan duration'}
                  error={errors.custom_duration_in_days?.message}
                  {...register('custom_duration_in_days')}
                />
              </div>
            )}
          </div>

          {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

          <Button type="submit" className="w-full" isLoading={isLoading}>
            Create investment
          </Button>
        </form>
      </Card>
    </div>
  );
}
